import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { Fragment, useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa";

import { Label, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiProviderMetadataChatAvailabilityChoices,
  ProviderAvailability,
  useUpdateProviderProfileMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

interface StatusOption {
  value: ProviderAvailability;
  label: string;
  description: string;
}

const STATUS_OPTIONS: Record<ApiProviderMetadataChatAvailabilityChoices, StatusOption> = {
  [ApiProviderMetadataChatAvailabilityChoices.Available]: {
    value: ProviderAvailability.Available,
    label: "Available",
    description: "Receive new chats",
  },
  [ApiProviderMetadataChatAvailabilityChoices.Unavailable]: {
    value: ProviderAvailability.Unavailable,
    label: "Away",
    description: "No new chats assigned",
  },
};

export const ChatStatus: React.FC = () => {
  const { loginProviderDetailsQuery } = useAdminData();
  const providerMetadata = loginProviderDetailsQuery.data?.getLoginProvider?.providermetadata;

  const [status, setStatus] = useState<ApiProviderMetadataChatAvailabilityChoices>(
    providerMetadata?.chatAvailability ?? ApiProviderMetadataChatAvailabilityChoices.Unavailable,
  );

  useEffect(() => {
    if (providerMetadata?.chatAvailability) setStatus(providerMetadata.chatAvailability);
  }, [providerMetadata?.chatAvailability]);

  const { mutateAsync: updateProviderProfile, isPending } = useUpdateProviderProfileMutation({});

  const handleSelect = async (option: ProviderAvailability, close: () => void) => {
    try {
      const response = await updateProviderProfile({
        providerData: {
          chatAvailability: option,
        },
      });

      if (!response.updateProviderProfile?.status) {
        notifyError(response.updateProviderProfile?.message || "Failed to update chat status.");
        return;
      }

      const returned = response.updateProviderProfile.provider?.providermetadata?.chatAvailability;
      if (returned) setStatus(returned);
      close();
      await loginProviderDetailsQuery.refetch();
    } catch {
      notifyError("Failed to update chat status.");
    }
  };

  return (
    <div className="flex items-center justify-between border-b border-border-subtle px-4 py-[14px]">
      <Label size="small" displayType="loud" colorType="secondary">
        Chat status
      </Label>

      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              disabled={isPending}
              className={clsx(
                "flex items-center gap-1.5 rounded-full border bg-bg-surface px-2 py-0.5 transition-colors focus:outline-hidden disabled:opacity-50",
                open ? "border-border-medium" : "border-border-subtle hover:border-border-medium",
              )}
            >
              <FaCircle
                className={clsx(
                  "h-2 w-2 shrink-0",
                  status === ApiProviderMetadataChatAvailabilityChoices.Available
                    ? "text-non-semantic-fill-green-strong"
                    : "text-non-semantic-fill-red-strong",
                )}
              />
              <Small colorType="primary" className="whitespace-nowrap">
                {STATUS_OPTIONS[status].label}
              </Small>
              <ChevronDownIcon
                className={clsx("h-4 w-4 text-icon-secondary transition-transform duration-150", open && "rotate-180")}
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 top-[calc(100%+4px)] z-10 w-max min-w-full rounded-lg border border-border-subtle bg-bg-surface p-1 shadow-xs">
                {(Object.entries(STATUS_OPTIONS) as [ApiProviderMetadataChatAvailabilityChoices, StatusOption][]).map(
                  ([key, option]) => (
                    <button
                      key={key}
                      disabled={isPending}
                      onClick={() => handleSelect(option.value, close)}
                      className={clsx(
                        "flex w-full flex-col items-start rounded-lg px-2 py-2 text-left transition-colors disabled:opacity-50",
                        key === status ? "bg-bg-selected" : "hover:bg-bg-hovered",
                      )}
                    >
                      <Small colorType="primary" displayType="loud" className="whitespace-nowrap">
                        {option.label}
                      </Small>
                      <Small colorType="tertiary" className="whitespace-nowrap">
                        {option.description}
                      </Small>
                    </button>
                  ),
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};
